import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button } from "components/common";
import { getPaymentRequest, retryPaymentRequestRelease } from "redux/erp/paymentRequestSlice";
import UnderlineInput from "../common/UnderlineInput";

/**
 * "Send to HSBC again" where the failed release is shown (2026-10-22), not only under
 * ERP journals → ERP & bank. Same action and same guards: the server sends the SAME
 * signed payment, only when HSBC cannot already have it (allow_retry_release).
 */
const RetryReleaseButton = ({ paymentRequest: pr, suggestedReason = "" }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(suggestedReason);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  if (!pr?.permissions?.allow_retry_release) return null;

  const send = async () => {
    if (busyRef.current || !reason.trim()) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const res = await dispatch(retryPaymentRequestRelease({ id: pr.id, reason: reason.trim() })).unwrap();
      toast.success(res?.message || "Payment queued to be sent to HSBC again.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.error || e?.message || "Could not send the payment again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
      dispatch(getPaymentRequest(pr.id));
    }
  };

  if (!open) {
    return (
      <Button size="sm" className="tw-mt-2" onClick={() => { setReason(suggestedReason); setOpen(true); }}>
        <i className="bi bi-send" aria-hidden="true" /> Send to HSBC again
      </Button>
    );
  }
  return (
    <div className="tw-mt-2 tw-space-y-2 tw-text-geist-fg">
      <UnderlineInput aria-label="Why send again" value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}
        placeholder="Why it is sent again, e.g. gateway fixed; nothing reached HSBC" />
      <div className="tw-flex tw-gap-2">
        <Button size="sm" loading={busy} aria-busy={busy} disabled={!reason.trim()} onClick={send}>
          {busy ? "Sending…" : "Send again"}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
};

RetryReleaseButton.propTypes = { paymentRequest: PropTypes.object, suggestedReason: PropTypes.string };

export default RetryReleaseButton;
